import React, { useMemo } from 'react';
import type {
  LearningEngagement,
  LearningProgress,
  TopCourse,
  LeaderboardEntry,
  DepartmentPerformance,
  CompletionTrend,
  CategoryBreakdownEntry,
  CertificationDistribution,
  SkillAssessment,
  RoleDistributionEntry,
  LearningHoursData,
  ProgressDistribution,
  CourseCompletionEntry,
  UserReportEntry,
} from '@/services/PagesServices/AnalyticsService';
import {
  computeInsights,
  computeHealthScore,
  computeFunnelData,
  computeProgressSegments,
  computeLineChart,
  computeDonutData,
  computeRoleDonut,
} from '../utils';
import HealthScoreCard from './HealthScoreCard';
import EngagementCard from './EngagementCard';
import OverviewCharts from './OverviewCharts';
import OverviewChartsExtra from './OverviewChartsExtra';

interface OverviewTabProps {
  engagement: LearningEngagement | null;
  learningProgress: LearningProgress | null;
  topCourses: TopCourse[];
  leaderboard: LeaderboardEntry[];
  deptPerformance: DepartmentPerformance[];
  completionTrends: CompletionTrend[];
  categoryBreakdown: CategoryBreakdownEntry[];
  certDistribution: CertificationDistribution[];
  skillsAssessment: SkillAssessment[];
  roleDistribution: RoleDistributionEntry[];
  learningHours: LearningHoursData[];
  progressDistribution: ProgressDistribution | null;
  courseCompletion: CourseCompletionEntry[];
  userReport: UserReportEntry[];
}

const OverviewTab: React.FC<OverviewTabProps> = ({
  engagement, learningProgress, topCourses, leaderboard,
  deptPerformance, completionTrends, categoryBreakdown,
  certDistribution, skillsAssessment, roleDistribution,
  learningHours, progressDistribution, courseCompletion, userReport,
}) => {
  const insights = useMemo(
    () => computeInsights(courseCompletion, deptPerformance, userReport, engagement),
    [courseCompletion, deptPerformance, userReport, engagement]
  );

  const healthScore = useMemo(
    () => computeHealthScore(insights, engagement, deptPerformance),
    [insights, engagement, deptPerformance]
  );

  const funnelData = useMemo(
    () => computeFunnelData(engagement, progressDistribution),
    [engagement, progressDistribution]
  );

  const progressSegments = useMemo(
    () => computeProgressSegments(progressDistribution),
    [progressDistribution]
  );

  const chartData = useMemo(() => {
    if (!learningHours || learningHours.length === 0) return null;
    const base = computeLineChart(learningHours.map(d => ({ value: d.hours, label: d.week })));
    if (!base) return null;
    const points = base.points.map((p, i) => ({
      ...p,
      hours: learningHours[i].hours,
      week: learningHours[i].week,
    }));
    return { ...base, points };
  }, [learningHours]);

  const donutData = useMemo(
    () => computeDonutData(certDistribution),
    [certDistribution]
  );

  const roleDonutData = useMemo(
    () => computeRoleDonut(roleDistribution),
    [roleDistribution]
  );

  return (
    <>
      {/* Platform Health Score + Learning Engagement */}
      <div className="analytics-grid-2">
        <HealthScoreCard
          healthScore={healthScore}
          insights={insights}
          engagement={engagement}
        />
        {engagement && <EngagementCard engagement={engagement} />}
      </div>

      <OverviewCharts
        funnelData={funnelData}
        progressSegments={progressSegments}
        chartData={chartData}
        learningProgress={learningProgress}
        topCourses={topCourses}
        leaderboard={leaderboard}
        deptPerformance={deptPerformance}
      />

      <OverviewChartsExtra
        completionTrends={completionTrends}
        categoryBreakdown={categoryBreakdown}
        certDistribution={certDistribution}
        donutData={donutData}
        skillsAssessment={skillsAssessment}
        roleDistribution={roleDistribution}
        roleDonutData={roleDonutData}
      />
    </>
  );
};

export default React.memo(OverviewTab);
